// Реальная голосовая сессия поверх WebSocket: подключение, авто-переподключение
// с экспоненциальной задержкой, отправка аудио/текста/прерывания, разбор
// входящих сообщений через ProtocolAdapter.

import { config, wsUrlWithAuth, type AppConfig } from '../config'
import type {
  ConnectionState,
  SessionEvent,
  SessionEventHandler,
  VoiceSession,
} from '../types'
import { DefaultProtocol, type ProtocolAdapter } from './protocol'

const MAX_RECONNECT_DELAY = 15000
const BASE_RECONNECT_DELAY = 800

export class WebSocketVoiceSession implements VoiceSession {
  private ws: WebSocket | null = null
  private handlers = new Set<SessionEventHandler>()
  private protocol: ProtocolAdapter
  private _state: ConnectionState = 'idle'
  private reconnectAttempts = 0
  private reconnectTimer: number | null = null
  private intentionalClose = false

  constructor(cfg: AppConfig = config, protocol?: ProtocolAdapter) {
    this.protocol = protocol ?? new DefaultProtocol(cfg)
  }

  get state(): ConnectionState {
    return this._state
  }

  on(handler: SessionEventHandler): () => void {
    this.handlers.add(handler)
    return () => this.handlers.delete(handler)
  }

  private emit(event: SessionEvent): void {
    this.handlers.forEach((h) => h(event))
  }

  private setState(state: ConnectionState, detail?: string): void {
    this._state = state
    this.emit({ type: 'connection', state, detail })
  }

  connect(): void {
    this.intentionalClose = false
    this.openSocket()
  }

  private openSocket(): void {
    this.clearReconnect()
    this.setState(this.reconnectAttempts > 0 ? 'reconnecting' : 'connecting')

    let ws: WebSocket
    try {
      ws = new WebSocket(wsUrlWithAuth())
    } catch (e) {
      this.setState('error', String((e as Error)?.message ?? e))
      this.scheduleReconnect()
      return
    }
    ws.binaryType = 'arraybuffer'
    this.ws = ws

    ws.onopen = () => {
      this.reconnectAttempts = 0
      this.setState('connected')
      this.safeSend(this.protocol.encodeControl({ kind: 'start' }))
    }

    ws.onmessage = (ev) => {
      const events = this.protocol.decode(ev.data as string | ArrayBuffer)
      events.forEach((e) => this.emit(e))
    }

    ws.onerror = () => {
      this.emit({ type: 'error', message: 'Ошибка WebSocket-соединения' })
    }

    ws.onclose = () => {
      this.ws = null
      if (this.intentionalClose) {
        this.setState('closed')
        return
      }
      this.setState('reconnecting')
      this.scheduleReconnect()
    }
  }

  disconnect(): void {
    this.intentionalClose = true
    this.clearReconnect()
    if (this.ws && this.ws.readyState === WebSocket.OPEN) {
      this.safeSend(this.protocol.encodeControl({ kind: 'stop' }))
    }
    this.ws?.close()
    this.ws = null
    this.setState('closed')
  }

  sendAudio(pcm: Int16Array): void {
    if (this.ws?.readyState !== WebSocket.OPEN) return
    this.safeSend(this.protocol.encodeAudio(pcm))
  }

  sendText(text: string): void {
    const trimmed = text.trim()
    if (!trimmed) return
    this.safeSend(this.protocol.encodeControl({ kind: 'text', text: trimmed }))
  }

  interrupt(): void {
    this.safeSend(this.protocol.encodeControl({ kind: 'interrupt' }))
  }

  private safeSend(data: ArrayBuffer | string): void {
    if (this.ws?.readyState !== WebSocket.OPEN) return
    try {
      this.ws.send(data)
    } catch {
      /* соединение умирает — onclose разрулит переподключение */
    }
  }

  private scheduleReconnect(): void {
    this.clearReconnect()
    this.reconnectAttempts++
    const delay = Math.min(
      MAX_RECONNECT_DELAY,
      BASE_RECONNECT_DELAY * 2 ** (this.reconnectAttempts - 1),
    )
    this.reconnectTimer = window.setTimeout(() => this.openSocket(), delay)
  }

  private clearReconnect(): void {
    if (this.reconnectTimer != null) {
      clearTimeout(this.reconnectTimer)
      this.reconnectTimer = null
    }
  }
}
